// src/components/main/produzione/assegnazioniGantt/GanttDragLayer.tsx

import React, { useEffect, useRef } from "react";
import { useDragLayer } from "react-dnd";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { GANTT_OL_TYPE_PREFIX, GanttOLDragItem } from "../GanttBar";

// ============================================
// CONSTANTS
// ============================================

const SCROLL_ZONE = 80; // px dal bordo per attivare lo scroll
const SCROLL_SPEED_MAX = 20; // px per frame (velocità massima)

// ============================================
// COMPONENT
// ============================================

const GanttDragLayer = () => {
    const theme = useTheme();
    const offsetRef = useRef<{ x: number; y: number } | null>(null);

    const { isDragging, item, currentOffset, itemType } = useDragLayer((monitor) => ({
        item: monitor.getItem() as GanttOLDragItem | null,
        itemType: monitor.getItemType() as string | null,
        currentOffset: monitor.getClientOffset(),
        isDragging: monitor.isDragging(),
    }));

    // Aggiorna il ref con l'offset corrente (senza causare re-render)
    offsetRef.current = currentOffset;

    // Auto-scroll durante il drag vicino ai bordi del viewport
    useEffect(() => {
        if (!isDragging) return;

        let animationFrameId: number;
        let isRunning = true;

        const autoScroll = () => {
            if (!isRunning) return;

            const offset = offsetRef.current;
            if (offset) {
                const { y } = offset;
                const viewportHeight = window.innerHeight;

                if (y < SCROLL_ZONE) {
                    // Vicino al bordo superiore: scrolla verso l'alto
                    const intensity = 1 - y / SCROLL_ZONE;
                    const speed = Math.ceil(SCROLL_SPEED_MAX * intensity);
                    window.scrollBy(0, -speed);
                } else if (y > viewportHeight - SCROLL_ZONE) {
                    // Vicino al bordo inferiore: scrolla verso il basso
                    const intensity = 1 - (viewportHeight - y) / SCROLL_ZONE;
                    const speed = Math.ceil(SCROLL_SPEED_MAX * intensity);
                    window.scrollBy(0, speed);
                }
            }

            animationFrameId = requestAnimationFrame(autoScroll);
        };

        animationFrameId = requestAnimationFrame(autoScroll);

        return () => {
            isRunning = false;
            cancelAnimationFrame(animationFrameId);
        };
    }, [isDragging]);

    // Mostra solo per drag OdL
    if (!isDragging || !currentOffset || !itemType?.startsWith(GANTT_OL_TYPE_PREFIX)) {
        return null;
    }

    const olItem = item as GanttOLDragItem & { codiceItem?: string; tecnicaDisplay?: string };

    return (
        <Box
            sx={{
                position: "fixed",
                pointerEvents: "none",
                zIndex: 9999,
                left: currentOffset.x - 60,
                top: currentOffset.y - 20,
            }}
        >
            <Box
                sx={{
                    backgroundColor: theme.palette.info.main,
                    color: "#fff",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    boxShadow: theme.shadows[4],
                    minWidth: 120,
                    textAlign: "center",
                }}
            >
                <Typography variant="caption" fontWeight="bold">
                    {olItem?.codiceItem || `OL #${olItem?.ordineLavoroId}`}
                </Typography>
                {olItem?.tecnicaDisplay && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                        {olItem.tecnicaDisplay}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default GanttDragLayer;
